"use client";

import {
  UnifiedChatContext,
  type UnifiedChatContextValue,
} from "./UnifiedChatProvider";
import type {
  ChatEntry,
  ChatSelectors,
  ChatStreamEvent,
  OpenChatMode,
  PageContext,
} from "./core/types";
import {
  isChatBusy,
  mintChatId,
  type UnifiedChatStoreState,
} from "./core/unifiedChat";
import type { PageTarget } from "@/components/analysis/agentic/pages/pageTarget";
import { trackError } from "@/components/error/trackError";
import { useOptionalSessionWorkspace } from "@/components/providers/org_provider";
import { useIsMobile } from "@/components/ui/hooks/use-mobile";
import { workspaceChatBackend } from "@/components/unified-chat/core/orgChatBackend";
import { pageTargetOptions } from "@/hooks/queries/analysis/pageTargetOptions";
import { missionControlChatsOptions } from "@/hooks/queries/mission-control/missionControlChatsOptions";
import { fireAndForget } from "@/lib/vercelUtils";
import { notNull } from "@/utils/helpers";
import type { AnalysisChatV2Id } from "@/utils/types";
import {
  mutationOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
import {
  useContext,
  useEffect,
  useEffectEvent,
  useRef,
  useSyncExternalStore,
} from "react";
import { useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";

const UNIFIED_CHAT_ROUTE = /^\/chat(\/|$)/;

export const isUnifiedChatRoute = (pathname: string): boolean =>
  UNIFIED_CHAT_ROUTE.test(pathname);

const useUnifiedChatContext = (): UnifiedChatContextValue => {
  const context = useContext(UnifiedChatContext);
  if (!context) {
    throw new Error(
      "Unified chat is disabled; gate this component on useIsUnifiedChatEnabled()"
    );
  }
  return context;
};

// Covers the first render, before the effect that opens the chat has run.
const LOADING_ENTRY: ChatEntry = { type: "loading" };

export const useIsUnifiedChatEnabled = (): boolean =>
  useContext(UnifiedChatContext) !== null;

/** The chat shown in the floating window and the panel. An unknown id is treated as a new chat. */
export const useActiveChatState = (): ChatEntry => {
  const { unifiedChat, orgId: workspaceId } = useUnifiedChatContext();
  useEffect(() => {
    unifiedChat.ensureActiveChat({ orgId: workspaceId });
  }, [unifiedChat, workspaceId]);
  return useStore(unifiedChat.store, (state) => {
    const activeChatId = state.activeChatIds[workspaceId];
    return (
      (activeChatId ? state.chats[activeChatId] : undefined) ?? LOADING_ENTRY
    );
  });
};

/** A chat shown by id, e.g. full screen from the URL. Does not change the active chat. */
export const useChatState = (
  chatId: AnalysisChatV2Id,
  mode: OpenChatMode = "existing"
): ChatEntry => {
  const { unifiedChat, orgId: workspaceId } = useUnifiedChatContext();
  useEffect(() => {
    unifiedChat.openChat({ chatId, orgId: workspaceId, mode });
  }, [unifiedChat, chatId, workspaceId, mode]);
  return useStore(
    unifiedChat.store,
    (state) => state.chats[chatId] ?? LOADING_ENTRY
  );
};

/** Makes the chat shown full screen the active one once it is open, so the dock reopens on it. */
export const useActivateLoadedChat = (entry: ChatEntry) => {
  const { unifiedChat, orgId: workspaceId } = useUnifiedChatContext();
  const loadedChatId = entry.type === "loaded" ? entry.state.id : undefined;
  useEffect(() => {
    if (loadedChatId) {
      unifiedChat.activateChat({ orgId: workspaceId, chatId: loadedChatId });
    }
  }, [unifiedChat, workspaceId, loadedChatId]);
};

/** Creates an empty chat so its page renders without a fetch. Does not change the active chat. */
export const useCreateChat = () => {
  const { unifiedChat, orgId: workspaceId } = useUnifiedChatContext();
  return () => {
    const chatId = mintChatId();
    unifiedChat.openChat({ chatId, orgId: workspaceId, mode: "new" });
    return chatId;
  };
};

export const useIsChatFromOtherWorkspace = (
  chatId: AnalysisChatV2Id
): boolean => {
  const { unifiedChat, orgId: workspaceId } = useUnifiedChatContext();
  return useStore(unifiedChat.store, (state) => {
    const chatWorkspaceId = state.chatOrgIds[chatId];
    return notNull(chatWorkspaceId) && chatWorkspaceId !== workspaceId;
  });
};

/** False only for a loaded chat the server has not saved yet. Does not open the chat. */
export const useIsChatPersisted = (
  chatId: AnalysisChatV2Id | null
): boolean => {
  const { unifiedChat } = useUnifiedChatContext();
  return useStore(unifiedChat.store, (state) => {
    const entry = chatId ? state.chats[chatId] : undefined;
    return entry?.type !== "loaded" || entry.state.isPersisted;
  });
};

export const deleteWorkspaceChatMutationOptions = () =>
  mutationOptions({ mutationFn: workspaceChatBackend.deleteChat });

export const useChatSelectors = (): ChatSelectors => {
  const { unifiedChat, orgId: workspaceId } = useUnifiedChatContext();
  const queryClient = useQueryClient();
  const { data: chatSummaries, isLoading: isChatListLoading } = useQuery(
    missionControlChatsOptions(workspaceChatBackend, workspaceId)
  );
  const aiWorkingChatIds = useStore(
    unifiedChat.store,
    useShallow((state) =>
      Object.values(state.chats)
        .map((entry) =>
          entry?.type === "loaded" && isChatBusy(entry.state.status)
            ? entry.state.id
            : undefined
        )
        .filter(notNull)
    )
  );
  const deleteChat = useMutation({
    ...deleteWorkspaceChatMutationOptions(),
    onSuccess: (_, chatId) => {
      unifiedChat.removeChat(chatId);
      return queryClient.invalidateQueries({
        queryKey: [workspaceChatBackend.listQueryKey],
      });
    },
  });
  return {
    selectChat: (chatId) =>
      unifiedChat.selectChat({ orgId: workspaceId, chatId }),
    addNewChat: () => unifiedChat.startNewChat({ orgId: workspaceId }),
    deleteChat: deleteChat.mutateAsync,
    availableChatList: chatSummaries?.map((chat) => ({
      ...chat,
      isAiWorking: aiWorkingChatIds.includes(chat.id),
    })),
    isChatListLoading,
  };
};

export const useUnifiedAgentViewState = () => {
  const { unifiedChat } = useUnifiedChatContext();
  return {
    unifiedAgentViewState: useStore(
      unifiedChat.store,
      (state) => state.viewState
    ),
    setUnifiedAgentViewState: unifiedChat.setViewState,
  };
};

// The event hooks are no-ops while unified chat is disabled, so any surface can subscribe unconditionally.
const useStreamEvents = (
  isRelevant: (
    event: ChatStreamEvent,
    context: UnifiedChatContextValue
  ) => boolean,
  handler: (event: ChatStreamEvent) => void
) => {
  const context = useContext(UnifiedChatContext);
  const onEvent = useEffectEvent((event: ChatStreamEvent) => {
    if (context && isRelevant(event, context)) {
      handler(event);
    }
  });
  const unifiedChat = context?.unifiedChat;
  useEffect(
    () => unifiedChat?.subscribeToEvents((event) => onEvent(event)),
    [unifiedChat]
  );
};

export const useChatStreamEvents = (
  chatId: AnalysisChatV2Id,
  handler: (event: ChatStreamEvent) => void
) => useStreamEvents((event) => event.chatId === chatId, handler);

export const useActiveChatStreamEvents = (
  handler: (event: ChatStreamEvent) => void
) =>
  useStreamEvents(
    (event, { unifiedChat, orgId }) =>
      event.chatId === unifiedChat.store.getState().activeChatIds[orgId],
    handler
  );

/** For pages that apply a turn's side effects even after the user switched to another chat. */
export const useAnyChatStreamEvents = (
  handler: (event: ChatStreamEvent) => void
) => useStreamEvents(() => true, handler);

/** Call before a link click leaves the full chat page, so the chat continues floating on the next page. No-op on other pages and while unified chat is disabled. */
export const useFloatChatAfterLeavingChatPage = (): (() => void) => {
  const unifiedChat = useContext(UnifiedChatContext)?.unifiedChat;
  const pathname = usePathname();
  // The dock is full screen on phones and would cover the page the user opened.
  const isMobile = useIsMobile({});
  return () => {
    if (!unifiedChat || !isUnifiedChatRoute(pathname)) return;
    unifiedChat.setViewState(isMobile ? "closed" : "floating");
  };
};

/** Opens the pages the agent navigates to. Mount once, above every page. */
export const useAgentNavigation = () => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const sessionWorkspace = useOptionalSessionWorkspace();
  const floatChatAfterLeavingChatPage = useFloatChatAfterLeavingChatPage();
  const navigateToTarget = async (target: PageTarget) => {
    const resolution = await queryClient.fetchQuery(pageTargetOptions(target));
    if (resolution.status !== "found") {
      throw new Error(`Agent navigation target is unavailable: ${target.page}`);
    }
    // Org pages render the selected org, so the target org is selected first.
    const targetWorkspaceId = "orgId" in target ? target.orgId : null;
    if (
      notNull(targetWorkspaceId) &&
      sessionWorkspace?.org?.id !== targetWorkspaceId
    ) {
      sessionWorkspace?.setOrgId(targetWorkspaceId);
    }
    floatChatAfterLeavingChatPage();
    router.push(resolution.url);
  };
  useAnyChatStreamEvents(({ event, mode }) => {
    // Intentionally never navigates on resume: a resume replays the turn from its first event, so it would repeat navigations the user may have left.
    if (event.type !== "navigate" || mode === "resume") return;
    fireAndForget(
      navigateToTarget(event.target).catch((error: unknown) =>
        trackError({
          error: error instanceof Error ? error : new Error(String(error)),
          componentStack: null,
          eventId: null,
          errorPlace: "unifiedChat.agentNavigation",
        })
      )
    );
  });
};

/** Null while unified chat is disabled. */
export const useOpenUnifiedAgentPanel = (): (() => void) | null => {
  const context = useContext(UnifiedChatContext);
  return context ? () => context.unifiedChat.setViewState("panel") : null;
};

/** Call only from a page's top-level component: every turn sent while the page is open awaits this getter once. */
export const useRegisterPageContext = (
  getter: () => PageContext | undefined | Promise<PageContext | undefined>
) => {
  const unifiedChat = useContext(UnifiedChatContext)?.unifiedChat;
  const getterRef = useRef(getter);
  useEffect(() => {
    getterRef.current = getter;
  });
  useEffect(
    () => unifiedChat?.registerPageContext(async () => getterRef.current()),
    [unifiedChat]
  );
};

const noopSubscribe = () => () => {};

export type PageWorkStatus = "idle" | "preparing" | "ai-working";

// A preparing turn counts for every page: it awaits the getter of the page that is open.
const getPageWorkStatus = (
  state: UnifiedChatStoreState,
  isPage: (pageContext: PageContext) => boolean
): PageWorkStatus => {
  const statuses = Object.values(state.chats)
    .map((entry) => (entry?.type === "loaded" ? entry.state.status : null))
    .filter(notNull);
  const isAiWorking = statuses.some(
    (status) =>
      status.type === "ai-working" &&
      notNull(status.pageContext) &&
      isPage(status.pageContext)
  );
  if (isAiWorking) return "ai-working";
  return statuses.some((status) => status.type === "preparing")
    ? "preparing"
    : "idle";
};

/** Whether any chat is running a turn that was sent from a matching page. False while unified chat is disabled. */
export const useIsAiWorkingOnPage = (
  isPage: (pageContext: PageContext) => boolean
): boolean => {
  const store = useContext(UnifiedChatContext)?.unifiedChat.store;
  return useSyncExternalStore(
    store?.subscribe ?? noopSubscribe,
    () =>
      !!store && getPageWorkStatus(store.getState(), isPage) === "ai-working",
    () => false
  );
};

/** Calls `onChange` with the current status on mount and on every change. No-op while unified chat is disabled. */
export const usePageWorkStatusChange = (
  isPage: (pageContext: PageContext) => boolean,
  onChange: (status: PageWorkStatus) => void
) => {
  const store = useContext(UnifiedChatContext)?.unifiedChat.store;
  const lastStatusRef = useRef<PageWorkStatus | null>(null);
  const reportStatus = useEffectEvent((state: UnifiedChatStoreState) => {
    const status = getPageWorkStatus(state, isPage);
    if (status === lastStatusRef.current) return;
    lastStatusRef.current = status;
    onChange(status);
  });
  useEffect(() => {
    if (!store) return;
    lastStatusRef.current = null;
    reportStatus(store.getState());
    return store.subscribe((state) => reportStatus(state));
  }, [store]);
};
